import * as React from 'react';
import PageModel from '@app/Model/PageModel';
import UserModel from '@app/Model/UserModel';
import PageLoader from '@app/Page/PageLoader';
import EventInterface from '@app/Interface/EventInterface';
import EventItem from '@app/Components/Event/EventItem';
import EventItemDetail from '@app/Components/Event/EventItemDetail';
import { PageSection, Title } from '@patternfly/react-core';
import htmlParser from '@app/utils/htmlParser';
import axios from "@app/Security/AxiosAuthentication";
import ApiEndpoints from '@app/utils/ApiEndpoints';
import RollbarHelper from '@app/utils/RollbarHelper';
import ItemLoader from '@app/Components/ItemLoader';

interface EventPageProps {
    fpuId: number,
    pageData: PageModel,
    path: string,
    search: string,
    currentUser: UserModel
}

const EventPage: React.FC<EventPageProps> = ({ fpuId, pageData, path, search, currentUser }) => {
    // List of all upcoming events
    const [events, setEvents] = React.useState<EventInterface[]>([]);
    const [eventDetail, setEventDetail] = React.useState<EventInterface | null>();
    const [eventPath, setEventPath] = React.useState('');

    const [loadingEvents, setLoadingEvents] = React.useState(true);

    // Get all upcoming events on page load
    React.useEffect(() => {
        const controller = new AbortController();

        let mounted = true;

        async function getEvents() {
            let fetchedEvents: Array<EventInterface> = [];
            try {
                const baseUrl: string = process.env.API_BASEURL ?? '';
                const endPoint: string = ApiEndpoints.EVENT_COLLECTIONS + `?page=1&fpu_id=${fpuId}&page_id=${pageData.id}` ?? "";
                const url = baseUrl + endPoint;
                const eventsResponse = await axios.get(url, {
                    withCredentials: true,
                    signal: controller.signal
                });
                const eventsResponseItems: EventInterface[] = eventsResponse.data['hydra:member'] ?? [];
                fetchedEvents = eventsResponseItems.map(event => {
                    return {
                        id: event.id,
                        slug: event.slug,
                        title: htmlParser.htmlDecode(event.title),
                        content: event.content,
                        eventLocation: event.eventLocation ?? null,
                        eventStartDate: new Date(event.eventStartDate),
                        eventEndDate: !event.eventAllDay && event.eventEndDate ? new Date(event.eventEndDate) : null,
                        eventAllDay: event.eventAllDay
                    };
                });
            } catch (error) {
                RollbarHelper.logRequestError(error, "getEvents()");
            } finally {
                if (mounted) {
                    setEvents(fetchedEvents);
                    setLoadingEvents(false);
                }
            }
        }

        // set default values first
        setLoadingEvents(true);
        setEventDetail(null);

        getEvents();

        return () => {
            mounted = false;
            controller?.abort();
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Get event details
    React.useEffect(() => {
        const passedEventSlug = htmlParser.getParameterValueFromUrl('event', search);
        const strippedPath = htmlParser.removeQuerystringFromPath(path);

        if (passedEventSlug) {
            const matchingEvent = events.find(event => event.slug === passedEventSlug);
            setEventDetail(matchingEvent ?? null);
        } else {
            setEventDetail(null);
        }

        setEventPath(strippedPath);
    }, [events, path, search]);

    if (eventDetail) {

        return (
            <>
                {loadingEvents
                    ? <PageLoader />
                    : <EventItemDetail event={eventDetail} path={eventPath} pageId={pageData.id} currentUser={currentUser} />
                }
            </>
        );

    } else {

        return (
            <>
                <PageSection>
                    <Title headingLevel="h1" className="pf-u-mb-md">{pageData.title}</Title>
                    <div className="pf-c-content">
                        {htmlParser.htmlToJSX(pageData.content ?? '')}
                    </div>
                </PageSection>
                {loadingEvents
                    ?<PageSection><ItemLoader /></PageSection> 
                    : <PageSection>
                        {events.length === 0 &&
                            <p>
                                <em>No upcoming events to display</em>
                            </p>
                        }
                        {events.map(event => (
                            <EventItem key={event.id} event={event} path={path} pageId={pageData.id} currentUser={currentUser} />
                        ))}
                    </PageSection>
                }
            </>
        );
    }
};

export default EventPage;